import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

export default class BlogPostsList extends Component {
  #cachedCards = [];

  #cachedCardCount = 0;

  connectedCallback() {
    super.connectedCallback?.();

    // DOM elements
    this.searchInput = this.querySelector('#blog-search__input');
    this.keywordButtons = this.querySelectorAll('.blog-search__keyword .keyword');
    this.resetButton = this.querySelector('[ref="resetButton"]'); // clear search
    this.searchButton = this.querySelector('.search-button');     // search button
    this.resultsContainer = this.querySelector('.featured-blog-live-search__results');
    this.cardsContainer = this.querySelector('[ref="grid"]'); // container for blog cards

    // Filter state
    this.currentTag = null;
    this.currentTextQuery = '';
    this.onSearchDebounced = debounce(() => this.onSearch(), 120);

    // Bind events
    this.bindEvents();
    this.#cacheCards();

    // Highlight first keyword at page load
    if (this.keywordButtons.length > 0) {
      this.keywordButtons[0].classList.add('is-active');
      this.currentTag = this.keywordButtons[0].dataset.filter.toLowerCase();
      this.applyFilters();
    }

    // Mark "Most Popular" cards on initial load
    this.markMostPopularCards();
  }

  /* ===================== CARDS GETTER ===================== */
  get cards() {
    // Always get current cards in DOM (pagination safe)
    return Array.from(this.querySelectorAll('[ref="cards[]"]'));
  }

  /* ===================== EVENTS ===================== */
  bindEvents() {
    // Tag buttons
    this.keywordButtons.forEach(button => {
      button.addEventListener('click', () => {
        const tag = button.dataset.filter.toLowerCase();

        // Remove previous active states
        this.keywordButtons.forEach(btn => btn.classList.remove('is-active'));

        // Mark the clicked button as active
        button.classList.add('is-active');

        // Update filter state and apply
        this.currentTag = tag;
        this.currentTextQuery = '';
        this.applyFilters();
      });
    });

    // Text search input
    this.searchInput?.addEventListener('input', e => {
      const query = e.target.value.trim().toLowerCase();
      this.currentTextQuery = query;
      this.onSearchDebounced();
    });

    // Reset button
    this.resetButton?.addEventListener('click', () => {
      if (this.searchInput) {
        this.searchInput.value = '';
      }
      this.currentTextQuery = '';
      this.onSearch();
    });

    const savedSearch = localStorage.getItem('search_blog');
    if (savedSearch && this.searchInput) {
      this.searchInput.value = savedSearch;
      localStorage.removeItem('search_blog');
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  onSearch() {
    const keyWordsWrapper = this.querySelector('.blog-suggested__keywords');
    keyWordsWrapper?.classList.toggle('is-hidden', this.currentTextQuery.length > 0);

    if (this.resetButton)  this.resetButton.style.display = this.currentTextQuery.length > 0 ? 'flex' : 'none';
    if (this.searchButton)  this.searchButton.style.display = this.currentTextQuery.length == 0 ? 'block' : 'none';
    this.applyFilters();
  }

  /* ===================== APPLY FILTERS ===================== */
  applyFilters() {
    this.#ensureCardCache();
    const terms = this.currentTextQuery.split(/\s+/).filter(Boolean);
    let visibleCount = 0;

    this.#cachedCards.forEach(({ card, tags, searchText, isMostPopular }) => {
      let visible = true;

      // Tag filter
      if (this.currentTag && this.currentTag !== 'all' && !this.currentTextQuery) {
        visible = tags.has(this.currentTag);
      }

      // Text filter
      if (visible && this.currentTextQuery) {
        visible = terms.every(term => searchText.includes(term));
      }

      // Show/hide card
      const shouldHide = !visible;
      if (card.hidden !== shouldHide) {
        card.hidden = shouldHide;
      }

      // Highlight "Most Popular"
      card.classList.toggle('is-most-popular', isMostPopular);

      if (visible) {
        visibleCount += 1;
      }
    });

    // Update results text
    this.updateResultsText(visibleCount);
  }

  /* ===================== RESULTS TEXT ===================== */
  getVisibleCount() {
    return this.cards.filter(card => !card.hidden).length;
  }

  updateResultsText(visibleCount = this.getVisibleCount()) {
    if (!this.resultsContainer) return;

    if (!this.currentTextQuery) {
      this.resultsContainer.style.display = 'none';
      this.resultsContainer.textContent = '';
      return;
    }

    this.resultsContainer.textContent = `${visibleCount} Result${visibleCount !== 1 ? 's' : ''} for “${this.currentTextQuery}”`;
    this.resultsContainer.style.display = 'block';
  }

  clearResultsText() {
    if (this.resultsContainer) {
      this.resultsContainer.textContent = '';
    }
  }

  /* ===================== MOST POPULAR ===================== */
  markMostPopularCards() {
    this.#ensureCardCache();
    this.#cachedCards.forEach(({ card, isMostPopular }) => {
      card.classList.toggle('is-most-popular', isMostPopular);
    });
  }

  /* ===================== BUTTON STATES ===================== */
  updateActiveButton(activeButton) {
    this.keywordButtons.forEach(btn =>
      btn.classList.toggle('is-active', btn === activeButton)
    );
  }

  #ensureCardCache() {
    const currentCards = this.cards;
    const needsRefresh =
      this.#cachedCardCount !== currentCards.length ||
      (this.#cachedCards.length > 0 && this.#cachedCards[0].card !== currentCards[0]) ||
      (this.#cachedCards.length > 0 && this.#cachedCards[this.#cachedCards.length - 1].card !== currentCards[currentCards.length - 1]);

    if (needsRefresh) {
      this.#cacheCards();
    }
  }

  #cacheCards() {
    this.#cachedCards = this.cards.map((card) => {
      const tagList = (card.dataset.tags || '')
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);

      const isMostPopular = tagList.includes('most popular');
      const searchText = (card.dataset.search || '').toLowerCase();

      return {
        card,
        tags: new Set(tagList),
        searchText,
        isMostPopular,
      };
    });

    this.#cachedCardCount = this.#cachedCards.length;
  }
}

/* ===================== REGISTER CUSTOM ELEMENT ===================== */
if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
